import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import {
  AuthArgs,
  SearchConfigService,
  TryItem,
  TryItemInput,
} from "../services/search-config.service";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // the upload endpoint rejects larger images

const emptyForm = (): TryItemInput => ({
  query: "",
  title: "",
  image_url: null,
  position: 0,
  active: true,
  market: null,
});

export default function TryItemsPanel() {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const args: AuthArgs = { auth, setAuth, logout };

  const [items, setItems] = useState<TryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<TryItem | "new" | null>(null);
  const [form, setForm] = useState<TryItemInput>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await SearchConfigService.listTryItems({
        auth,
        setAuth,
        logout,
      });
      setItems(Array.isArray(rows) ? rows : []);
    } catch (e: any) {
      setError(e?.message || "Failed to load cards");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (item: TryItem | "new") => {
    setEditing(item);
    setFormError(null);
    setForm(
      item === "new"
        ? { ...emptyForm(), position: items.length }
        : {
            query: item.query,
            title: item.title ?? "",
            image_url: item.image_url,
            position: item.position,
            active: item.active,
            market: item.market,
          },
    );
  };

  const close = () => {
    setEditing(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPickImage = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setFormError("Please choose an image file.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setFormError("Image is larger than 5 MB.");
      return;
    }
    setFormError(null);
    setUploading(true);
    try {
      const url = await SearchConfigService.uploadImage(args, file);
      setForm((f) => ({ ...f, image_url: url }));
    } catch (e: any) {
      setFormError(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const query = form.query.trim();
    if (!query) return setFormError("Search text is required.");
    if (form.market && !/^[A-Za-z]{2}$/.test(form.market))
      return setFormError("Market must be a 2-letter country code, e.g. VN.");

    const body: TryItemInput = {
      ...form,
      query,
      title: form.title?.trim() || null,
      market: form.market ? form.market.toUpperCase() : null,
    };
    setSaving(true);
    try {
      if (editing === "new") await SearchConfigService.createTryItem(args, body);
      else if (editing)
        await SearchConfigService.updateTryItem(args, editing._id, body);
      close();
      await load();
    } catch (e: any) {
      setFormError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: TryItem) => {
    if (!window.confirm(`Remove the card "${item.title || item.query}"?`)) return;
    try {
      await SearchConfigService.deleteTryItem(args, item._id);
      await load();
    } catch (e: any) {
      setError(e?.message || "Delete failed");
    }
  };

  const toggle = async (item: TryItem) => {
    try {
      await SearchConfigService.updateTryItem(args, item._id, {
        query: item.query,
        title: item.title,
        image_url: item.image_url,
        position: item.position,
        active: !item.active,
        market: item.market,
      });
      await load();
    } catch (e: any) {
      setError(e?.message || "Update failed");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0 small">
          Cards pinned here show first in "Try searching for" on the search
          home, ahead of the automatic ones. Without a photo, the app picks one
          from your own data.
        </p>
        <button className="btn btn-primary" onClick={() => open("new")}>
          + Add card
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm border-0">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="ps-4">Photo</th>
                <th>Card</th>
                <th>Market</th>
                <th>Order</th>
                <th>Status</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="text-center py-5 text-muted">
                    No pinned cards yet.
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item._id}>
                  <td className="ps-4">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt=""
                        style={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    ) : (
                      <span className="text-muted small">Auto</span>
                    )}
                  </td>
                  <td>
                    <div className="fw-semibold">{item.title || item.query}</div>
                    <div className="small text-muted">
                      Searches: <span className="font-monospace">{item.query}</span>
                    </div>
                  </td>
                  <td>{item.market || <span className="text-muted">All</span>}</td>
                  <td>{item.position}</td>
                  <td>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={item.active}
                        onChange={() => toggle(item)}
                        aria-label="Active"
                      />
                    </div>
                  </td>
                  <td className="text-end pe-4">
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => open(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => remove(item)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <form className="modal-content" onSubmit={save}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {editing === "new" ? "Add card" : "Edit card"}
                </h5>
                <button type="button" className="btn-close" onClick={close} />
              </div>
              <div className="modal-body">
                {formError && (
                  <div className="alert alert-danger py-2">{formError}</div>
                )}

                <div className="mb-3">
                  <label className="form-label">Search text *</label>
                  <input
                    className="form-control"
                    value={form.query}
                    maxLength={100}
                    placeholder="e.g. yoga"
                    onChange={(e) => setForm({ ...form, query: e.target.value })}
                  />
                  <div className="form-text">
                    What the app searches when the card is tapped.
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Card title</label>
                  <input
                    className="form-control"
                    value={form.title ?? ""}
                    maxLength={60}
                    placeholder="Leave empty to use the search text"
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Photo</label>
                  {form.image_url && (
                    <div className="mb-2 d-flex align-items-center gap-3">
                      <img
                        src={form.image_url}
                        alt=""
                        style={{
                          width: 96,
                          height: 96,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          setForm({ ...form, image_url: null });
                          if (fileRef.current) fileRef.current.value = "";
                        }}
                      >
                        Remove photo
                      </button>
                    </div>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="form-control"
                    disabled={uploading}
                    onChange={(e) => onPickImage(e.target.files?.[0])}
                  />
                  <div className="form-text">
                    {uploading
                      ? "Uploading…"
                      : "Up to 5 MB. Without a photo the app picks one automatically."}
                  </div>
                </div>

                <div className="row">
                  <div className="col-6 mb-3">
                    <label className="form-label">Market</label>
                    <input
                      className="form-control text-uppercase"
                      value={form.market ?? ""}
                      maxLength={2}
                      placeholder="All"
                      onChange={(e) =>
                        setForm({ ...form, market: e.target.value || null })
                      }
                    />
                    <div className="form-text">Country code, empty = all.</div>
                  </div>
                  <div className="col-6 mb-3">
                    <label className="form-label">Order</label>
                    <input
                      type="number"
                      min={0}
                      max={1000}
                      className="form-control"
                      value={form.position}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          position: Math.max(0, parseInt(e.target.value, 10) || 0),
                        })
                      }
                    />
                    <div className="form-text">Lower shows first.</div>
                  </div>
                </div>

                <div className="form-check">
                  <input
                    id="try-active"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                  <label htmlFor="try-active" className="form-check-label">
                    Active
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={close}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving || uploading}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
